import React, { useState, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Printer, Bluetooth, QrCode, AlertCircle, CheckCircle, 
  Wifi, WifiOff, Loader2, Settings, RefreshCw
} from 'lucide-react';

/**
 * QR Code Mobile Printer Component
 * Supports Web Bluetooth API for thermal printers (ESC/POS compatible)
 * 
 * Props:
 * - qrData: string - The data to encode in the QR code
 * - itemName: string - Item name to print alongside QR
 * - onPrintSuccess: function - Callback when print succeeds
 * - onPrintError: function - Callback when print fails
 */
const QRCodePrinter = ({ 
  qrData, 
  itemName = 'Inventory Item',
  onPrintSuccess,
  onPrintError 
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [device, setDevice] = useState(null);
  const [characteristic, setCharacteristic] = useState(null);
  const [printerName, setPrinterName] = useState('');
  const [printCount, setPrintCount] = useState(1);
  
  // Common thermal printer service UUIDs
  const PRINTER_SERVICE_UUIDS = [
    '000018f0-0000-1000-8000-00805f9b34fb', // Generic thermal printer
    '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Some ESC/POS printers
    '0000ff00-0000-1000-8000-00805f9b34fb', // Common BLE printer
  ];

  const CHARACTERISTIC_UUIDS = [
    '00002af1-0000-1000-8000-00805f9b34fb', // Write characteristic
    '49535343-8841-43f4-a8d4-ecbe34729bb3', // ESC/POS write
    '0000ff01-0000-1000-8000-00805f9b34fb', // Common write
  ];

  // Check if Web Bluetooth is supported
  const isBluetoothSupported = () => {
    return 'bluetooth' in navigator;
  };

  // Connect to Bluetooth printer
  const connectPrinter = useCallback(async () => {
    if (!isBluetoothSupported()) {
      toast.error('Web Bluetooth not supported in this browser. Use Chrome or Edge.');
      return;
    }

    setIsConnecting(true);

    try {
      // Request device with thermal printer services
      const btDevice = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: PRINTER_SERVICE_UUIDS
      });

      setPrinterName(btDevice.name || 'Thermal Printer');
      
      // Connect to GATT server
      const server = await btDevice.gatt.connect();
      
      // Try to find the printer service
      let service = null;
      for (const serviceUuid of PRINTER_SERVICE_UUIDS) {
        try {
          service = await server.getPrimaryService(serviceUuid);
          break;
        } catch (e) {
          // Service not found, try next
        }
      }

      if (!service) {
        // Try to get any available service
        const services = await server.getPrimaryServices();
        if (services.length > 0) {
          service = services[0];
        } else {
          throw new Error('No printer services found');
        }
      }

      // Find the write characteristic
      let writeChar = null;
      for (const charUuid of CHARACTERISTIC_UUIDS) {
        try {
          writeChar = await service.getCharacteristic(charUuid);
          break;
        } catch (e) {
          // Characteristic not found, try next
        }
      }

      if (!writeChar) {
        // Try to get any writable characteristic
        const characteristics = await service.getCharacteristics();
        for (const char of characteristics) {
          if (char.properties.write || char.properties.writeWithoutResponse) {
            writeChar = char;
            break;
          }
        }
      }

      if (!writeChar) {
        throw new Error('No writable characteristic found');
      }

      setDevice(btDevice);
      setCharacteristic(writeChar);
      setIsConnected(true);
      toast.success(`Connected to ${btDevice.name || 'Thermal Printer'}`);

      // Listen for disconnection
      btDevice.addEventListener('gattserverdisconnected', () => {
        setIsConnected(false);
        setDevice(null);
        setCharacteristic(null);
        toast.warning('Printer disconnected');
      });

    } catch (error) {
      console.error('Bluetooth connection error:', error);
      if (error.name === 'NotFoundError') {
        toast.error('No printer selected or found');
      } else {
        toast.error(`Connection failed: ${error.message}`);
      }
      onPrintError?.(error);
    } finally {
      setIsConnecting(false);
    }
  }, [onPrintError]);

  // Disconnect printer
  const disconnectPrinter = useCallback(() => {
    if (device?.gatt?.connected) {
      device.gatt.disconnect();
    }
    setDevice(null);
    setCharacteristic(null);
    setIsConnected(false);
    setPrinterName('');
    toast.success('Printer disconnected');
  }, [device]);

  // ESC/POS commands for QR code printing
  const generateQRCodeCommands = (data, size = 8) => {
    const textEncoder = new TextEncoder();
    const dataBytes = textEncoder.encode(data);
    const dataLength = dataBytes.length + 3;
    const pL = dataLength % 256;
    const pH = Math.floor(dataLength / 256);
    
    return new Uint8Array([
      // Initialize printer
      0x1B, 0x40, // ESC @ - Initialize
      
      // Set QR code model
      0x1D, 0x28, 0x6B, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00, // Model 2
      
      // Set QR code size (1-16)
      0x1D, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x43, size,
      
      // Set error correction level (L=48, M=49, Q=50, H=51)
      0x1D, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x45, 0x31, // Level M
      
      // Store QR code data
      0x1D, 0x28, 0x6B, pL, pH, 0x31, 0x50, 0x30,
      ...dataBytes,
      
      // Print QR code
      0x1D, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x51, 0x30,
    ]);
  };

  // Generate text commands
  const generateTextCommands = (text, centered = true) => {
    const textEncoder = new TextEncoder();
    const textBytes = textEncoder.encode(text);
    
    const commands = [
      // Center align if requested
      ...(centered ? [0x1B, 0x61, 0x01] : [0x1B, 0x61, 0x00]),
      // Bold on
      0x1B, 0x45, 0x01,
      // Print text
      ...textBytes,
      // New line
      0x0A,
      // Bold off
      0x1B, 0x45, 0x00,
    ];
    
    return new Uint8Array(commands);
  };

  // Generate feed and cut commands
  const generateEndCommands = () => {
    return new Uint8Array([
      // Feed paper
      0x1B, 0x64, 0x03, // Feed 3 lines
      // Cut paper (if supported)
      0x1D, 0x56, 0x41, 0x03, // Partial cut
    ]);
  };

  // Print QR code label
  const printQRLabel = useCallback(async () => {
    if (!isConnected || !characteristic) {
      toast.error('Printer not connected');
      return;
    }

    if (!qrData) {
      toast.error('No QR data to print');
      return;
    }

    setIsPrinting(true);

    try {
      for (let i = 0; i < printCount; i++) {
        // Print item name
        const nameCommands = generateTextCommands(itemName);
        await characteristic.writeValue(nameCommands);
        await new Promise(r => setTimeout(r, 100));

        // Print separator line
        const separatorCommands = generateTextCommands('----------------');
        await characteristic.writeValue(separatorCommands);
        await new Promise(r => setTimeout(r, 100));

        // Print QR code
        const qrCommands = generateQRCodeCommands(qrData);
        
        // Split into chunks if needed (some printers have buffer limits)
        const chunkSize = 20;
        for (let j = 0; j < qrCommands.length; j += chunkSize) {
          const chunk = qrCommands.slice(j, j + chunkSize);
          await characteristic.writeValue(chunk);
          await new Promise(r => setTimeout(r, 50));
        }

        // Print QR code text below
        const codeCommands = generateTextCommands(qrData);
        await characteristic.writeValue(codeCommands);
        await new Promise(r => setTimeout(r, 100));

        // End commands (feed and cut)
        const endCommands = generateEndCommands();
        await characteristic.writeValue(endCommands);
        
        // Small delay between labels
        if (i < printCount - 1) {
          await new Promise(r => setTimeout(r, 500));
        }
      }

      toast.success(`Printed ${printCount} QR label(s)`);
      onPrintSuccess?.();
    } catch (error) {
      console.error('Print error:', error);
      toast.error(`Print failed: ${error.message}`);
      onPrintError?.(error);
    } finally {
      setIsPrinting(false);
    }
  }, [isConnected, characteristic, qrData, itemName, printCount, onPrintSuccess, onPrintError]);

  // Fallback: Generate printable image and trigger browser print
  const printViaImage = useCallback(() => {
    // Create a simple print window with QR code
    const printWindow = window.open('', '_blank', 'width=400,height=500');
    
    if (!printWindow) {
      toast.error('Please allow pop-ups to print');
      return;
    }

    // Generate QR code using canvas API
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Code - ${itemName}</title>
        <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"></script>
        <style>
          body {
            font-family: Arial, sans-serif;
            text-align: center;
            padding: 20px;
          }
          .label {
            border: 1px dashed #ccc;
            padding: 15px;
            margin: 10px auto;
            max-width: 300px;
            page-break-after: always;
          }
          .item-name {
            font-weight: bold;
            font-size: 14px;
            margin-bottom: 10px;
          }
          .qr-container {
            margin: 10px auto;
          }
          .qr-text {
            font-family: monospace;
            font-size: 10px;
            margin-top: 10px;
            word-break: break-all;
          }
          @media print {
            .no-print { display: none; }
            body { padding: 0; }
            .label { border: none; }
          }
        </style>
      </head>
      <body>
        ${Array(printCount).fill(`
          <div class="label">
            <div class="item-name">${itemName}</div>
            <div id="qrcode" class="qr-container"></div>
            <div class="qr-text">${qrData}</div>
          </div>
        `).join('')}
        <div class="no-print">
          <button onclick="window.print()">Print</button>
          <button onclick="window.close()">Close</button>
        </div>
        <script>
          document.querySelectorAll('.qr-container').forEach((container, index) => {
            container.id = 'qr' + index;
            new QRCode(container, {
              text: "${qrData}",
              width: 128,
              height: 128,
              correctLevel: QRCode.CorrectLevel.M
            });
          });
          setTimeout(() => window.print(), 500);
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  }, [qrData, itemName, printCount]);

  if (!isBluetoothSupported()) {
    return (
      <Card className="border-yellow-200 bg-yellow-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 text-yellow-700">
            <AlertCircle size={20} />
            <div>
              <p className="font-medium">Bluetooth printing not available</p>
              <p className="text-sm">Use Chrome or Edge browser for Bluetooth printing.</p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-2"
                onClick={printViaImage}
              >
                <Printer size={14} className="mr-2" />
                Print via Browser
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Connection Status */}
      <Card className={isConnected ? 'border-green-200 bg-green-50' : ''}>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${isConnected ? 'bg-green-100' : 'bg-gray-100'}`}>
                {isConnected ? (
                  <Bluetooth className="text-green-600" size={20} />
                ) : (
                  <WifiOff className="text-gray-400" size={20} />
                )}
              </div>
              <div>
                <p className="font-medium text-sm">
                  {isConnected ? printerName : 'No printer connected'}
                </p>
                <p className="text-xs text-gray-500">
                  {isConnected ? 'Ready to print' : 'Connect to a thermal printer'}
                </p>
              </div>
            </div>
            
            <div className="flex gap-2">
              {isConnected ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSettings(true)}
                    data-testid="printer-settings-btn"
                  >
                    <Settings size={14} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={disconnectPrinter}
                    data-testid="disconnect-printer-btn"
                  >
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  onClick={connectPrinter}
                  disabled={isConnecting}
                  data-testid="connect-printer-btn"
                >
                  {isConnecting ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={14} />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Bluetooth size={14} className="mr-2" />
                      Connect Printer
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Print Button */}
      {qrData && (
        <div className="flex gap-2">
          <Button
            className="flex-1"
            onClick={isConnected ? printQRLabel : printViaImage}
            disabled={isPrinting}
            data-testid="print-qr-btn"
          >
            {isPrinting ? (
              <>
                <Loader2 className="animate-spin mr-2" size={16} />
                Printing...
              </>
            ) : (
              <>
                <QrCode size={16} className="mr-2" />
                Print QR Label {printCount > 1 && `(×${printCount})`}
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => setPrintCount(Math.min(printCount + 1, 10))}
            data-testid="increase-count-btn"
          >
            +
          </Button>
          <Button
            variant="outline"
            onClick={() => setPrintCount(Math.max(printCount - 1, 1))}
            data-testid="decrease-count-btn"
          >
            -
          </Button>
        </div>
      )}

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Printer Settings</DialogTitle>
            <DialogDescription>Configure your thermal printer</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm">Connected Printer</span>
              <Badge variant="outline">{printerName}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Print Count</span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPrintCount(Math.max(printCount - 1, 1))}
                >
                  -
                </Button>
                <span className="w-8 text-center">{printCount}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPrintCount(Math.min(printCount + 1, 10))}
                >
                  +
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={disconnectPrinter}>
              <RefreshCw size={14} className="mr-2" />
              Reconnect Different Printer
            </Button>
            <Button onClick={() => setShowSettings(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QRCodePrinter;
